import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { bind, type UnbindFn } from 'bind-event-listener';

import { AtlassianLogo } from '@atlaskit/logo';
import { N0, N100, N20, N40 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { GlobalNavUserProfile } from '@/components/global-nav/user-profile-new/src/user-profile';
import { GlobalNavProvider } from '@/patterns/global-nav/src/nav-links-container';

const outerContainerStyles = css({
	position: 'relative',
	width: '100%',
});

const brandOuterContainerStyles = css({
	backgroundColor: token('color.background.accent.gray.subtlest', N20),
});

const containerStyles = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	height: '72px',
	margin: '0 auto',
	maxWidth: '1168px',
	// TODO: Use new breakpoints, info at https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples

	'@media (max-width: 1202px)': {
		marginInlineStart: token('space.200', '16px'),
		marginInlineEnd: token('space.200', '16px'),
	},
	// TODO: Use new breakpoints, info at https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples

	'@media (max-width: 664px)': {
		height: '54px',
	},
});

const rightSectionProfileStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200', '16px'),
});

const helpStyles = css({
	color: token('color.text.inverse', N0),
	textDecoration: 'none',
	'&:hover': {
		color: token('color.text.inverse', N0),
		textDecoration: 'none',
		cursor: 'pointer',
	},
});

const brandHelpStyles = css({
	color: token('color.background.accent.gray.bolder', N100),
	'&:hover': {
		color: token('color.background.accent.gray.bolder', N100),
	},
});

const profileMenuWrapperStyles = css({
	paddingInlineStart: token('space.100', '8px'),
	borderInlineStart: `solid 1px ${token('color.text.inverse', N0)}`,
});

const brandProfileMenuWrapperStyles = css({
	borderInlineStart: `solid 1px ${token('color.border', N40)}`,
});

type WidthMode = 'wide' | 'narrow';

const HELP_LINK = 'https://www.atlassian.com/company/contact/purchasing-licensing';

function getWidthMode(): WidthMode {
	return window.innerWidth > 664 ? 'wide' : 'narrow';
}

export const TopNav = () => {
	const [widthMode, setWidthMode] = useState<WidthMode>(getWidthMode());

	const appearance = widthMode === 'wide' ? 'inverse' : 'brand';

	useEffect(() => {
		function handleResize() {
			setWidthMode(getWidthMode());
		}

		const unbind: UnbindFn = bind(window, {
			type: 'resize',
			listener: handleResize,
		});

		return unbind;
	}, []);

	return (
		<div
			css={[outerContainerStyles, appearance === 'brand' && brandOuterContainerStyles]}
			className="outer-container"
		>
			{}
			<div css={containerStyles} style={{ zIndex: 3 }}>
				<AtlassianLogo appearance={appearance} size={widthMode === 'wide' ? 'medium' : 'small'} />

				<div css={rightSectionProfileStyles}>
					<a
						href={HELP_LINK}
						target="_blank"
						css={[helpStyles, appearance === 'brand' && brandHelpStyles]}
						rel="noreferrer"
					>
						Need Help?
					</a>
					<div
						css={[
							profileMenuWrapperStyles,
							appearance === 'brand' && brandProfileMenuWrapperStyles,
						]}
					>
						<GlobalNavProvider globalNavItemKeys={[]}>
							<GlobalNavUserProfile isUseNewLoggedInUserDropdown={true} />
						</GlobalNavProvider>
					</div>
				</div>
			</div>
		</div>
	);
};
